import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { axisOf, dragOffset, releaseTo } from '../lib/tabSwipe'

// Swiping the phone's page sideways to change tab (lib/tabSwipe.ts decides).
// The page follows the finger; on release it springs back, or the next tab
// comes in from the side the finger was moving toward. A press that starts on
// something with its own sideways gesture (a list row's delete swipe, a chip
// row that scrolls, a field, an open sheet) is left alone, and a mostly
// vertical move is a scroll.

/** What keeps its own sideways gesture. */
const OWN_GESTURE = '[data-swipe-row], input, textarea, select, [contenteditable="true"], [role="dialog"], .ml-scrim, [data-no-tab-swipe]'
const SETTLE_MS = 240

function ownsGesture(target: EventTarget | null, root: HTMLElement): boolean {
  if (!(target instanceof Element)) return true
  if (target.closest(OWN_GESTURE)) return true
  // A row that scrolls sideways (Running low, the recipe filters) scrolls.
  for (let el: Element | null = target; el && el !== root; el = el.parentElement) {
    const x = getComputedStyle(el).overflowX
    if ((x === 'auto' || x === 'scroll') && el.scrollWidth > el.clientWidth) return true
  }
  return false
}

export type SwipePhase = 'idle' | 'drag' | 'enter' | 'settle'

export function useTabSwipe(index: number, count: number, onIndex: (i: number) => void) {
  const [dx, setDx] = useState(0)
  const [phase, setPhase] = useState<SwipePhase>('idle')
  const [width, setWidth] = useState(390)
  const press = useRef<{ x: number; y: number; t: number; lastX: number; axis: 'x' | 'y' | null } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // A sideways swipe that ends on a card or a button must not also tap it.
  const swiped = useRef(false)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const settle = useCallback(() => {
    // One frame at the start position (the incoming page off to the side), then slide home.
    requestAnimationFrame(() => {
      setPhase('settle')
      setDx(0)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setPhase('idle'), SETTLE_MS)
    })
  }, [])

  const bind = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      swiped.current = false
      if (e.button !== 0 || ownsGesture(e.target, e.currentTarget)) return
      press.current = { x: e.clientX, y: e.clientY, t: e.timeStamp, lastX: e.clientX, axis: null }
      setWidth(e.currentTarget.getBoundingClientRect().width || window.innerWidth)
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const p = press.current
      if (!p) return
      const mx = e.clientX - p.x
      p.lastX = e.clientX
      if (!p.axis) {
        p.axis = axisOf(mx, e.clientY - p.y)
        if (p.axis === 'y') { press.current = null; return }
        if (p.axis === 'x') {
          try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* synthetic events in tests */ }
        }
      }
      if (p.axis === 'x') {
        setPhase('drag')
        setDx(dragOffset(mx, index, count))
      }
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      const p = press.current
      press.current = null
      if (!p || p.axis !== 'x') return
      swiped.current = true
      const mx = (e.clientX || p.lastX) - p.x
      const to = releaseTo(mx, e.timeStamp - p.t, width, index, count)
      if (to !== index) {
        // The new tab starts a third of the way in from the side the finger went toward.
        setPhase('enter')
        setDx((to > index ? 1 : -1) * width * 0.35)
        onIndex(to)
      }
      settle()
    },
    onClickCapture: (e: ReactMouseEvent) => {
      if (!swiped.current) return
      swiped.current = false
      e.preventDefault()
      e.stopPropagation()
    },
    onPointerCancel: () => {
      if (!press.current) return
      press.current = null
      settle()
    },
  }

  /** -1…1 while dragging, positive toward the next tab; 0 otherwise. */
  const progress = phase === 'drag' ? Math.max(-1, Math.min(1, -dx / width)) : 0
  return { bind, dx, phase, progress }
}
